import { useEffect, useReducer, useRef, useState } from 'react';
import { useBeforeUnload, useNavigate } from 'react-router-dom';

import { Button } from '@mui/material';
import { differenceInDays } from 'date-fns';

import MaisAtacado from '../../../Services/MaisAtacado';
import { initialState, negociacaoReducer } from '../reducers/useNegociacaoReducer'

import CardBox from '../../../components/CardBox/Card';
import SelectNegotiation from '../Components/SelectNegotiation/SelectNegotiation';
import SomaStatus from '../../../components/SomaStandard/SomaStatus';
import ConverteDinheiroFunction from '../../../Utils/ConverteDinheiroFunction';
import ConverteDataFunction from '../../../Utils/ConverteDataFunction';

import TitlesSelector from '../../../components/TitlesSelector/TitlesSelector';
import InputRange from '../../../components/InputRange/InputRange';
import NegociacoesModal from '../Components/NegocicoesModal';
import { formatPhone } from '../../../Utils/format';

const NegociacoesFaturas = () => {
    const navigate = useNavigate();
    const ref = useRef([])

    const [status, setStatus] = useState('wait');
    const [results, setResults] = useState([])
    const [stateNegocicao, dispatchNegociacao] = useReducer(negociacaoReducer, initialState);

    const percInicialEntrada = results?.paramsPagamento?.boleto?.entrada

    const compararDatas = () => differenceInDays(new Date(), new Date(results?.faturas?.[0]?.vencimento_real))

    const sendNegotiation = async (finalizado, email) => await MaisAtacado.Negociacao.setNegociacao({ ...ref.current, finalizado, email })

    const propsCardBox = {
        title: {
            name: 'Valores a pagar',
        },
        infos: [
            { left: 'Dívida total', right: ConverteDinheiroFunction(results?.divida?.total) },
            { left: 'Dívida total atualizada', right: ConverteDinheiroFunction(results?.divida?.totalComJuros) },
            { left: 'Dívida cobrança externa', right: ConverteDinheiroFunction(results?.divida?.externa) },
        ],
        infoHighlighted: {
            left: 'Dívida cobrança interna atualizada',
            right: ConverteDinheiroFunction(results?.divida?.internaComJuros)
        }
    }

    const propsTitlesSelector = {
        clickCheckbox: (selectedItems) => {
            dispatchNegociacao({ type: 'clickFatura', payload: { results, selectedItems } })
        },
        faturas: results?.faturas?.map(f => {
            return {
                idFormated: <>Fatura: {f.fatura.trim()}-{f.id_parcela.trim()}</>,
                valueFormated: <>
                    <br />Valor: {ConverteDinheiroFunction(f.saldo_principal_devido_com_juros)}<br />
                    Venc.: {ConverteDataFunction(f.vencimento_real)}
                </>,
                value: f.saldo_principal_devido_com_juros,
                id: `${f.fatura.trim()}${f.id_parcela.trim()}`,
                disabled: stateNegocicao.forma === 'boleto' ? true : false
            }
        }),
        resume: { title: 'Valor Selecionado', value: ConverteDinheiroFunction(stateNegocicao.totalSelecionadas?.semTaxa) }

    }

    const propsFormaPagamento = {
        handleChange: (e) => {
            dispatchNegociacao({
                type: 'clickFormaPagamento',
                payload: { results, formaPagamento: e.target.value }
            })
        },
        options: compararDatas() > 35 ? [
            { value: 'pix', option: 'Pix' },
            { value: 'cartao', option: 'Cartão de Crédito' },
            { value: 'boleto', option: 'Boleto Bancário' },
        ] : [
            { value: 'pix', option: 'Pix' },
            { value: 'cartao', option: 'Cartão de Crédito' },
        ],
        value: stateNegocicao.forma
    }

    const propsOpcoesParcelamento = () => ({
        handleChange: (e) => dispatchNegociacao({ type: 'clickParcelas', payload: { numParcelas: e.target.value } }),
        value: stateNegocicao.numParcelas || '',
        options: Object.entries(stateNegocicao.valorParcela?.formaPgto || {}).map(([key, value]) => {
            return {
                value: key,
                option: `${key} de ${ConverteDinheiroFunction(value)} - Total de ${ConverteDinheiroFunction(stateNegocicao.totalSelecionadas?.formaPgto[key])}`
            }
        })
    })

    const inputRangeProps = {
        range: {
            min: percInicialEntrada,
            max: 100,
            value: stateNegocicao?.entrada?.perc,
            minFormated: `${percInicialEntrada}%`,
            maxFormated: `${100}%`
        },
        value: `${ConverteDinheiroFunction(stateNegocicao?.entrada?.valor)} 
        (${stateNegocicao?.entrada?.perc}%)`,
        handleChange: (percentage) => {
            dispatchNegociacao({ type: 'clickEntrada', payload: { percentage } })
        },
    }

    const propsIntervaloBoletos = {
        handleChange: (e) => {
            dispatchNegociacao({
                type: 'clickFormaPagamento',
                payload: { results, formaPagamento: 'boleto', boletos: e.target.value }
            })
        },
        options: results?.paramsPagamento?.boleto ? Object.entries(results.paramsPagamento.boleto.intervalo)
            .map(([chave, valor]) => {
                return { value: chave, option: `${chave} - até ${valor} boletos` };
            }) : [],
        value: stateNegocicao.boletos
    }

    const disableBtnAceitarNegociacao = () => {
        const temFatura = stateNegocicao.selecionadas.length
        const forma = stateNegocicao.forma
        const parcelas = stateNegocicao.numParcelas
        const boletos = stateNegocicao.boletos

        if (forma === 'pix' && temFatura) return false
        else if (forma === 'cartao' && parcelas && temFatura) return false
        else if (forma === 'boleto' && parcelas && boletos && temFatura) return false
        else return true
    }

    useEffect(() => {
        setStatus('wait');
        (async () => {
            try {
                const json = (await MaisAtacado.Negociacao.getFaturas(stateNegocicao.forma)).data;
                setResults(json);
                const f = {
                    selecionadas: json.faturas,
                    totalSelecionadas: { semTaxa: json.divida.internaComJuros },
                    valorParcela: { semTaxa: json.divida.internaComJuros }
                }

                dispatchNegociacao({ type: 'setFaturas', payload: f })
                if (stateNegocicao.forma) {
                    dispatchNegociacao({
                        type: 'clickFormaPagamento',
                        payload: { results, formaPagamento: stateNegocicao.forma }
                    })
                }
                setStatus(json.faturas?.length || json.divida.externa > 0 ? 'result' : 'empty');

            } catch (err) {
                setResults(undefined);
                setStatus('error');
            }
        })();
        return () => {
            if (!ref.current.modalFinalizar) {
                sendNegotiation()
            }
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stateNegocicao.forma]);

    useBeforeUnload((e) => {
        e.preventDefault()
        sendNegotiation()
    });

    useEffect(() => {
        ref.current = {
            faturas: stateNegocicao.forma ? stateNegocicao.selecionadas : [],
            valorInicial: stateNegocicao.forma && stateNegocicao.totalSelecionadas?.semTaxa,
            valorFinal: stateNegocicao.forma &&
                stateNegocicao.totalSelecionadas?.formaPgto?.[stateNegocicao.numParcelas || 1] + (stateNegocicao.entrada?.valor || 0),
            formaPagamento: stateNegocicao.forma,
            valorParcela: stateNegocicao.forma === 'pix' ?
                undefined :
                stateNegocicao.valorParcela?.formaPgto?.[stateNegocicao.numParcelas],
            numeroParcelas: stateNegocicao.numParcelas,
            entrada: stateNegocicao.entrada?.perc,
            entradaValor: stateNegocicao.entrada?.valor,
            boleto: stateNegocicao.boletos,
            juros: results?.paramsPagamento?.[stateNegocicao.forma]?.juros,
            modalFinalizar: stateNegocicao.finalizar,
            taxaAdm: stateNegocicao.forma,
        }
    }, [stateNegocicao, results.paramsPagamento]); // 

    const renderFormaPagamento = () => (
        <>
            <p style={{ color: "#757575" }}>2.  Escolha as faturas a serem quitados:</p>
            <TitlesSelector key={stateNegocicao.forma === 'boleto' ? 'boleto' : ''}{...propsTitlesSelector} />

            {stateNegocicao.forma === 'cartao' && (
                <>
                    <p style={{ color: "#757575" }}>3. Escolha em quantas parcelas quer pagar:</p>
                    <SelectNegotiation {...propsOpcoesParcelamento()} />
                    <p className='p__negociacoesFaturas--info'>
                        Taxa administrativa do cartão será inclusa: {results.paramsPagamento.cartao.taxa}% por parcela.
                    </p>
                </>
            )}

            {stateNegocicao.forma === 'boleto' && (
                <>
                    <p className='p__negociacoesFaturas--info'>
                        Na opção boleto, todas as faturas obrigatoriamente vão ser adicionadas na negociação.
                    </p>
                    <p style={{ color: "#757575" }}>3. Escolha o valor da entrada:</p>
                    <InputRange {...inputRangeProps} />
                    <p style={{ color: "#757575" }}>4. Escolha o intervalo dos boletos:</p>
                    <SelectNegotiation {...propsIntervaloBoletos} />

                    {stateNegocicao.boletos && (
                        <>
                            <p style={{ color: "#757575" }}>5. Escolha a quantidade de boletos:</p>
                            <SelectNegotiation {...propsOpcoesParcelamento()} />
                            <p className='p__negociacoesFaturas--info'>
                                Taxa administrativa do boleto será inclusa: {results.paramsPagamento.boleto.taxa}% por parcela.
                            </p>
                        </>
                    )}
                </>
            )}
        </>
    )

    return (
        <>
            <NegociacoesModal
                data={ref.current}
                modals={{ confirmar: stateNegocicao.confirmar, finalizar: stateNegocicao.finalizar }}
                setModals={(modals) => dispatchNegociacao({ type: 'setModals', payload: modals })}
                sendNegotiation={sendNegotiation}
            />
            <SomaStatus status={status} emptyText={'Sem resultados para essa busca :)'}>
                <div className='div__negociacoesFaturas--container'>
                    <div>
                        <CardBox {...propsCardBox} />
                        <p className='p__negociacoesFaturas--info'>Faturas a vencer não estão incluídas.</p>

                        <p style={{ color: "#757575" }}>1. Escolha a forma de pagamento:</p>
                        <SelectNegotiation {...propsFormaPagamento} />

                        {stateNegocicao.forma && renderFormaPagamento()}
                    </div>

                    {results.contaExterna && <p>
                        Para negociar sua dívida externa,<br />
                        entre em contato com:<br /><br />
                        {results.contaExterna.nome}<br />
                        {formatPhone(results.contaExterna.telefone)}
                    </p>
                    }
                </div>

                <div className='div__negociacoesFaturas--footer'>
                    <Button variant="outlined" onClick={() => { navigate('/Financeiro/faturas') }}>CANCELAR</Button>
                    <Button
                        style={{ color: 'white', fontFamily: 'flama-basic' }}
                        variant="contained"
                        disabled={disableBtnAceitarNegociacao()}
                        onClick={() => dispatchNegociacao({ type: 'setModals', payload: { confirmar: true, finalizar: false } })}
                    >
                        enviar proposta
                    </Button>
                </div>
            </SomaStatus>
        </>
    )
}

export default NegociacoesFaturas